/**
 *
 * Header.jsx
 *
 * This component renders the navigation bar
 *
 */

import React, {Component} from 'react';
import { withRouter} from 'react-router-dom';
import AUTHENTICATION_CONTROLLER from '../../controllers/authentication';
import {connect} from 'react-redux';
import LanguageStringResolver from '../fields/language/LanguageStringResolver'
import Logger from '../../utils/logger';
import LoadingSpinner from '../spinners/LoadingSpinner';
import SystemErrors from '../inAppMessages/SystemErrors';
import NetworkErrors from '../inAppMessages/NetworkErrors';
import Products from '../authorization/Products';
import {FeatureFlagEnum, ProductEnum} from '../../utils/enums';
import {ApplicationMessagesIcons} from '../inAppMessages/ApplicationMessagesIcons';
import getClientConfig from '../../utils/getClientConfig';
import zoomInWidgetConfig from './zoomInWidgetConfig.json';
import moment from 'moment';
import { AuiSupernav, AuiHeader } from '@avalara/skylab-sdk/react';
import { getAccessToken } from '../../utils/tokenStorage';

const log = new Logger('HEADER');

const huliveProducts = [ProductEnum.LIVE_REPORTING];
const vatProducts = [ProductEnum.VAT_REPORTING];

// CSS classes to hide account menu items per requirements
const HEADER_HIDE_CLASSES = 'hide-account-id hide-account-products-usage hide-account-support-cases hide-account-billing hide-account-my-downloads hide-account-overview hide-account-companies hide-account-users-admins hide-account-security-settings';

const MasqueradeStaticHeaderComponent = (props) => {
  return (
    <div className="global-warning">
      <s-container>
        <p className="m0 text-align-center">
          <span className="error-text">
            <s-icon name="alert"/>
            <LanguageStringResolver string="global_loggedInAsAdmin" />
          </span>
          <s-icon name="close" role="img" aria-label="close"></s-icon>
        </p>
      </s-container>
    </div>
  );
}

const buildNavigationItems = (props, getComplianceCalendarUrl) => {
  const { featureFlags, globalSupportUrl } = getClientConfig();
  const enableDynamicReports = featureFlags?.EnableDynamicReports;
  const enableTemplatesMenu = featureFlags?.EnableTemplatesMenu;
  const enableManualDocuments = featureFlags?.EnableManualDocuments;
  const enableBulkActions = featureFlags?.EnableBulkActions;
  const enableGlobalSupportTab = featureFlags?.EnableGlobalSupportTab;
  const enableComplianceCalendarMenu = featureFlags?.EnableComplianceCalendarMenu;
  const enableLedgerReports = featureFlags?.[FeatureFlagEnum['ENABLE_LEDGER_REPORTS']];
  const enableUserManagementTiles = props.featureFlags?.EnableUserManagementTiles;
  const iamAuthEnabled = props.iamData?.isIamUser;

  const navItems = [];

  if (enableComplianceCalendarMenu) {
    navItems.push({
      label: "Dashboard",
      url: getComplianceCalendarUrl(),
      dataRef: "dashboard"
    });
  }

  navItems.push({
    label: "Returns",
    dataRef: "returns",
    menuItems: [
      {
        label: "Open returns",
        url: "/openReturns?defaultMonth=now&defaultYear=now",
        dataRef: "returns.openReturns"
      },
      {
        label: "Closed returns",
        url: "/closedReturns",
        dataRef: "returns.closedReturns"
      }
    ]
  });

  navItems.push({
    label: "Errors & Warnings",
    url: "/errorsAndWarnings",
    dataRef: "errors"
  });

  navItems.push({
    label: "Transactions",
    dataRef: "transactions",
    menuItems: [
      {
        label: "Transactions",
        url: "/TransactionSearch?exactMatch=1&searchType=0&defaultYear=now",
        dataRef: "transactions.search"
      },
      {
        label: "Import transactions",
        url: "/newUpload",
        dataRef: "transactions.import"
      },
      {
        label: "Processed documents",
        url: "/ProcessedDocuments",
        dataRef: "transactions.history"
      },
      ...(enableManualDocuments ? [{
        label: "Manual documents",
        url: "/ManualDocuments",
        dataRef: "transactions.manualDocuments"
      }] : []),
      ...(enableBulkActions ? [{
        label: "Bulk Operations",
        url: "/BulkActions",
        dataRef: "transactions.bulkActions"
      }] : []),
      ...(enableTemplatesMenu ? [{
        label: "Templates",
        url: "/templates",
        dataRef: "transactions.templates"
      }] : [])
    ]
  });

  navItems.push({
    label: "Reports",
    dataRef: "reports",
    menuItems: [
      {
        label: "Transaction reports",
        url: "/TransactionReports",
        dataRef: "reports.dataMining"
      },
      {
        label: "VAT code report",
        url: "/VATCodeReport",
        dataRef: "reports.analysis"
      },
      {
        label: "Live reporting transmission status",
        url: "/viewTransmissions",
        dataRef: "reports.transmissions"
      },
      {
        label: "IT - Merge XML Files",
        url: "/MergeXMLfiles",
        dataRef: "reports.mergeXml"
      },
      ...(enableDynamicReports ? [{
        label: "Dynamic Reports",
        url: "/DynamicReports",
        dataRef: "reports.dynamicReports"
      }] : [])
    ]
  });

  if (enableLedgerReports) {
    navItems.push({
      label: "Ledgers",
      dataRef: "ledgers",
      menuItems: [
        {
          label: "Sales & Purchase ledgers",
          url: "/SalesAndPurchase",
          dataRef: "ledgers.salesAndPurchase"
        }
      ]
    });
  }

  navItems.push({
    label: "Settings",
    dataRef: "settings",
    menuItems: [
      {
        label: "Account settings",
        url: "/AccountSettings",
        dataRef: "settings.account"
      },
      {
        label: "Companies",
        url: "/Companies",
        dataRef: "settings.companies"
      },
      {
        label: enableUserManagementTiles ? "Users and Permissions" : "Users",
        url: "/UserManagement",
        dataRef: "settings.userManagement"
      },
      ...(iamAuthEnabled ? [{
        label: "Company Permissions",
        url: "/CompanyPermissions",
        dataRef: "settings.companyPermissions"
      }] : []),
      {
        label: "VAT codes",
        url: "/VATCodes",
        dataRef: "settings.vatCodes"
      },
      {
        label: "VAT code simulator",
        url: "/VATCodeSimulator",
        dataRef: "settings.vatCodeSimulator"
      },
      {
        label: "Exchange rates",
        url: "/ExchangeRates",
        dataRef: "settings.exchangeRates"
      },
      {
        label: "Countries On Hold",
        url: "/CountriesOnHold",
        dataRef: "settings.countriesOnHold"
      },
      {
        label: "Live reporting setup",
        url: "/setupLiveReporting",
        dataRef: "settings.setup"
      },
      {
        label: "VAT groups",
        url: "/VATGroups",
        dataRef: "settings.vatGroups"
      }
    ]
  });

  if (enableGlobalSupportTab) {
    navItems.push({
      label: "Support",
      url: globalSupportUrl,
      dataRef: "support"
    });
  }

  return navItems;
};

class Header extends Component {
  constructor(props) {
    super(props);
    this.activeMenu = "returns";
    this.onLinkClick = this.onLinkClick.bind(this);
    this.requestCurrentAccessToken = this.requestCurrentAccessToken.bind(this);
    if (!window.iph) {
      console.error('ZoomIN iph widget is not initialized');
    } else {
      window.iph('init', zoomInWidgetConfig);
    }
    this.state = {
      hasLoadedDynamicMenu: false
    };
  }

  /**
   * Returns the current access token for the aui-header component.
   * Refreshes the token if it has less than 5 seconds remaining.
   * @returns {Promise<{accessToken: string}>} Promise resolving to access token object
   */
  async requestCurrentAccessToken() {
    const currentToken = getAccessToken();
    return { accessToken: currentToken };
  }
  
 /**
   * Constructs the Compliance Calendar URL with optional filing period parameters.
   *
   * If the current path includes 'openReturns', it computes and appends the `filingPeriod`
   * query parameter based on the provided or current date, using existing SVR logic.
   *
   * If 'openReturns' is not part of the path, it returns the base compliance calendar URL
   * without any query parameters (defaulting to current month view).
   *
   * @param {string} [url=''] - Optional base URL to override the default from config.
   *                             If not provided, the URL will be retrieved from getClientConfig().
   * @returns {string} - The final Compliance Calendar URL (with or without query parameters).
   */
 getComplianceCalendarUrl(url = '') {
  const { location } = this.props;
  const complianceCalendarUrl = url || getClientConfig().complianceCalendarUrl;

  if (!complianceCalendarUrl) return '#';

  if (!location.pathname.includes('openReturns')) {
    return complianceCalendarUrl;
  }

  const searchParams = new URLSearchParams(location.search);
  const defaultYear = searchParams.get('defaultYear');
  const defaultMonth = searchParams.get('defaultMonth');
  const year = searchParams.get('year');
  const month = searchParams.get('month');

  // Get current date for fallback
  const currentDate = moment();

  // Determine the target date
  let targetDate;
  if (defaultYear === 'now' && defaultMonth === 'now') {
    // If both defaultYear and defaultMonth are 'now', use current date
    targetDate = currentDate;
  } else if (defaultYear === 'now' && month) {
    // If defaultYear is 'now' but month is provided, use current year with provided month
    targetDate = moment(`${currentDate.format('YYYY')}-${month}-01`);
  } else if (year && month) {
    // If both year and month are provided, use them
    targetDate = moment(`${year}-${month}-01`);
  } else {
    // If no valid parameters are provided, use current date
    targetDate = currentDate;
  }

  // Get the next month
  const nextMonth = targetDate.clone().add(1, 'month');

  // Format the filing period
  const monthName = nextMonth.format('MMMM');
  const yearName = nextMonth.format('YYYY');

  // Construct the URL with the filing period
  return `${complianceCalendarUrl}?filingPeriod=${monthName}+${yearName}`;
}

  componentDidUpdate(prevProps) {
    log.debug('componentDidUpdate(): ', this.props.location.pathname);
    //For Adobe Analytics tracking only page name
    if(this.props.location.pathname !== prevProps.location.pathname)
    {
      window && window._satellite && window._satellite.track('page_view');
    }
    // dynamic menu loading logic:
    if (
      !this.state.hasLoadedDynamicMenu &&
      this.props.featureFlagsLoaded &&
      this.props.featureFlags?.EnableDynamicMenu
    ) {
      this.props.doLoadDynamicMenu(this.props.featureFlags.EnableSideNavigation);
      this.setState({ hasLoadedDynamicMenu: true });
    }
  }

  onLinkClick(event) {
    if(event && event.currentTarget && event.currentTarget.href && event.currentTarget.href.includes(this.props.location.pathname)) { 

      const dropdown = event.currentTarget.closest('s-dropdown');
      if (dropdown) {
        dropdown.removeAttribute('open');
      }
      this.props.history.push(`${this.props.location.pathname}${this.props.location.search}`);
      event.stopPropagation();
      event.preventDefault();
    }
  }

  navHandlerForCustomer(e, href) {
    e.preventDefault();
    this.closeAnyOpenDropdowns();
    if (href.includes('support')) {
      window.open(href, '_blank').focus();
    } else if (href && this.isRewrittenUrlSameDomain(href)) {
      const relativePath = href.replace(window.location.origin, '');
      this.props.history.push(relativePath);
    } else if (href && href.startsWith('/')) {
      this.props.history.push(href);
    } else if (href && href.includes('http') && !this.isRewrittenUrlSameDomain(href)) {
      window.location.href = href;
    }
  }

  closeAnyOpenDropdowns() {
    const openDropdowns = document.querySelectorAll('s-dropdown[open]');
    openDropdowns.forEach((dropdown) => dropdown.removeAttribute('open'));
  }

  onHelpClick(){
    if (!window.iph) {
      console.error('ZoomIN iph widget is not initialized');
      return;
    }
    window.iph('toggle');
  }

  isRewrittenUrlSameDomain = (url) => {
      return url.startsWith(window.location.origin);
  }
  
  // Dynamic Menu Rendering: As we are getting absolute URL from API, we need match the window.location.origin. 
  // If it's same as origin then return relative path else return the absolute path. (AvaApp and Support - absolute path, other menu item has relative path)
  getDynamicMenuURL = (url) => {
    return this.isRewrittenUrlSameDomain(url)
    ? url.replace(window.location.origin, '')
    : url;
  };

  setActiveMenuFromPathname() {
    const pathname = this.props.location.pathname;
    
    if (pathname.startsWith('/DynamicReports/')) {
      this.activeMenu = "reports";
      return;
    }

    switch (pathname) {
      case '/Dashboard':
        this.activeMenu = "dashboard";
        break;
      case '/openReturns':
      case '/closedReturns':
        this.activeMenu = "returns";
        break;
      case '/errorsAndWarnings':
        this.activeMenu = "errors";
        break;
      case '/TransactionSearch':
      case '/upload':
      case '/newUpload':
      case '/ProcessedDocuments':
      case '/ManualDocuments':
      case '/BulkActions':
      case '/templates':
        this.activeMenu = "transactions";
        break;
      case '/TransactionReports':
      case '/VATCodeReport':
      case '/viewTransmissions':
      case '/MergeXMLfiles':
      case '/DynamicReports':
        this.activeMenu = "reports";
        break;
      case '/SalesAndPurchase':
        this.activeMenu = "ledgers";
        break;
      case '/AccountSettings':
      case '/Companies':
      case '/UserManagement':
      case '/CompanyPermissions':
      case '/VATCodes':
      case '/VATCodeSimulator':
      case '/ExchangeRates':
      case '/CountriesOnHold':
      case '/setupLiveReporting':
      case '/VATGroups':
        this.activeMenu = "settings";
        break;
      default:
        // do nothing
        break;
    }
  }

  transformDynamicMenuItem(menu, iamAuthEnabled) {
    const menuItem = {
      label: menu.label,
      dataRef: menu.label.toLowerCase() === 'errors & warnings' ? 'errors' : menu.label.toLowerCase(),
    };

    if (menu.items?.length > 0 && Array.isArray(menu.items)) {
      const filteredSubItems = menu.items.filter((subItem) => {
        if (subItem.url.includes('CompanyPermissions')) {
          return iamAuthEnabled;
        }
        return true;
      });
      menuItem.menuItems = filteredSubItems;
    } else {
      const isDashboard = (menu.label || '').toLowerCase() === 'dashboard';
      const computedUrl = isDashboard ? this.getComplianceCalendarUrl(menu.url) : menu.url;
      menuItem.url = this.getDynamicMenuURL(computedUrl);
    }

    return menuItem;
  }

  getDynamicNavItems(iamAuthEnabled) {
    if (!this.props.dynamicMenuItems || this.props.dynamicMenuItems.length === 0) {
      return [];
    }
    return this.props.dynamicMenuItems.map((menu) => 
      this.transformDynamicMenuItem(menu, iamAuthEnabled)
    );
  }

  renderUserMenu(enableAvalaraIdentity, enableAvalaraIdentityJwt, identityHost, userName) {
    return (
      <div className="float-right position-relative user-menu">
        <s-menu>
          <button slot="menu-trigger" className="primary userName" aria-controls="menu1-id">
            <s-icon name="triangle-down" class="pad-left-sm"></s-icon>
            {userName}
            <s-icon name="triangle-up" class="pad-left-sm"></s-icon>
          </button>
          <ul slot="menu-items" id="menu1-id">
            {(enableAvalaraIdentity || enableAvalaraIdentityJwt) &&
              <li>
                <a href={`${identityHost}/changePassword`}>
                  <LanguageStringResolver string={'TitleChangePassword'}/>
                </a>
              </li>
            }
            <li>
              <button onClick={this.attemptLogout.bind(this)} data-testid='logout-btn'>
                <LanguageStringResolver string={'TitleLogOut'}/>
              </button>
            </li>
          </ul>
        </s-menu>
      </div>
    );
  }

  renderStaticMenu(vatProducts, huliveProducts, enableGlobalSupportTab, globalSupportUrl) {
    return (
      <div id="staticmenu">
        <Products show={vatProducts}>
          <AuiSupernav 
            data-testid="aui-supernav"
            active={this.activeMenu}
            navItems={buildNavigationItems(this.props, this.getComplianceCalendarUrl.bind(this))}
            navHandler={(e, url) => this.navHandlerForCustomer(e, url)}
          />
        </Products>

        <Products hide={vatProducts} show={huliveProducts}>
          <AuiSupernav 
            data-testid="aui-supernav"
            active={this.activeMenu}
            navItems={[
              {
                label: "Reports",
                dataRef: "reports",
                menuItems: [
                  {
                    label: "Live reporting transmission status",
                    url: "/viewTransmissions",
                    dataRef: "reports.transmissions"
                  }
                ]
              },
              {
                label: "Settings",
                dataRef: "settings",
                menuItems: [
                  {
                    label: "Live reporting setup",
                    url: "/setupLiveReporting",
                    dataRef: "settings.setup"
                  }
                ]
              },
              ...(enableGlobalSupportTab ? [{
                label: "Support",
                url: globalSupportUrl,
                dataRef: "support"
              }] : [])
            ]}
            navHandler={(e, url) => this.navHandlerForCustomer(e, url)}
          />
        </Products>
      </div>
    );
  }

  renderDynamicMenu(iamAuthEnabled) {
    const dynamicNavItems = this.getDynamicNavItems(iamAuthEnabled);
    return (
      <AuiSupernav 
        id="dynamicmenu" 
        active={this.activeMenu} 
        navHandler={(e, url) => this.navHandlerForCustomer(e, url)} 
        navItems={dynamicNavItems}
      />
    );
  }

  renderMainNavigation(displayStyle, vatProducts, huliveProducts, enableGlobalSupportTab, globalSupportUrl, iamAuthEnabled) {
    if (!this.props.featureFlagsLoaded || this.props.featureFlags.EnableSideNavigation) {
      return null;
    }

    return (
      <div className="main-navigation" style={displayStyle}>
        {!this.props.featureFlags?.EnableDynamicMenu && 
          this.renderStaticMenu(vatProducts, huliveProducts, enableGlobalSupportTab, globalSupportUrl)
        }
        
        {this.props.featureFlags?.EnableDynamicMenu && 
          this.renderDynamicMenu(iamAuthEnabled)
        }
      </div>
    );
  }

  renderLoggedInControls(config) {
    const {
      userName,
      displayStyle,
      allProducts,
      vatProducts,
      huliveProducts,
      authConfig,
      supportConfig,
      iamAuthEnabled
    } = config;

    const { guiUrl, changePasswordUrl } = getClientConfig();
    const showChatButton = !!this.props.featureFlags?.EnableAviChatbot;
    const showNotificationsButton = !!this.props.featureFlags?.[FeatureFlagEnum.ENABLE_NOTIFICATIONS_BUTTON];
    const userObject = {
      isLoggedIn: true,
      name: userName
    };

    return (
      <div>
        <LoadingSpinner/>

        <div style={displayStyle}>
          <AuiHeader
            className={HEADER_HIDE_CLASSES}
            productName="VAT Reporting"
            homeUrl={guiUrl || '/'}
            logoutUrl="/auth/logout"
            changePasswordUrl={changePasswordUrl}
            user={userObject}
            showChatButton={!!showChatButton}
            showNotificationsButton={showNotificationsButton}
            integrateLocaleSwitcher={false}
            integratewafflemenu={true}
            wafflemenuactiveitem="vat-reporting"
            requestCurrentAccessToken={this.requestCurrentAccessToken}
          />
        </div>

        {this.renderMainNavigation(
          displayStyle,
          vatProducts,
          huliveProducts,
          supportConfig.enableGlobalSupportTab,
          supportConfig.globalSupportUrl,
          iamAuthEnabled
        )}

        <div className={'clear'}></div>
        <SystemErrors />
        <NetworkErrors />
        <div className={localStorage.masqueradeUser ? '': 'invisible'} style={displayStyle}>
          <MasqueradeStaticHeaderComponent />
        </div>
      </div>
    );
  }

  renderLoggedOutControls() {
    const { guiUrl } = getClientConfig();
    const showChatButton = !!this.props.featureFlags?.EnableAviChatbot;
    const showNotificationsButton = !!this.props.featureFlags?.[FeatureFlagEnum.ENABLE_NOTIFICATIONS_BUTTON];
    const displayStyle = this.props.hideMenu ? {"display" : "none"} :{"display":"block"};

    return (
      <div>
        <div style={displayStyle}>
          <AuiHeader
            className={HEADER_HIDE_CLASSES}
            productName="VAT Reporting"
            homeUrl={guiUrl || '/'}
            user={{ isLoggedIn: false }}
            showChatButton={!!showChatButton}
            showNotificationsButton={showNotificationsButton}
            integrateLocaleSwitcher={false}
            integratewafflemenu={false}
          />
        </div>
        <div className={'clear'}></div>
        <SystemErrors />
        <NetworkErrors />
      </div>
    );
  }

  render() {
    log.debug('render(): ', this.props);
    
    this.setActiveMenuFromPathname();
    
    const { enableAvalaraIdentity, enableAvalaraIdentityJwt, identityHost, featureFlags, globalSupportUrl } = getClientConfig();
    const isLoggedIn = this.props.loggedIn;
    const userName = this.props.userName;
    const displayStyle = this.props.hideMenu ? {"display" : "none"} :{"display":"block"};
    const enableGlobalSupportTab = featureFlags?.EnableGlobalSupportTab;
    const iamAuthEnabled = this.props.iamData?.isIamUser;

    const loggedInControls = this.renderLoggedInControls({
      userName,
      displayStyle,
      allProducts: this.props.products,
      vatProducts,
      huliveProducts,
      authConfig: {
        enableAvalaraIdentity,
        enableAvalaraIdentityJwt,
        identityHost
      },
      supportConfig: {
        enableGlobalSupportTab,
        globalSupportUrl
      },
      iamAuthEnabled
    });

    const loggedOutControls = this.renderLoggedOutControls();

    log.debug('isLoggedIn: ', isLoggedIn);
    
    return (
      <div>
        <header>
          {isLoggedIn ? loggedInControls : loggedOutControls}
        </header>
        <div className="display-mobile-only">
          <s-container>
            <s-alert status="info">
              <ApplicationMessagesIcons messageStatus={"info"} />
              <LanguageStringResolver string={'mobileNotSupported'}/>
            </s-alert>
          </s-container>
        </div>
      </div>
    );
  }

  attemptLogout(evt) {
    log.debug('attemptLogout(): ', this.props, evt);
    evt.preventDefault();

    this.props.doLogout(this.props.onLogoutSuccess, (error) => {
      log.error('attemptLogout(): FAILED TO LOG OUT: ', error);
    });
  }
}

// Which props do we want to inject, given the global state?
function mapStateToProps(state) {
  log.debug('mapStateToProps: ', state);
  return {
    authentication: state.authentication,
    dynamicMenuItems: state.authentication && state.authentication.dynamicMenuItems,
    userName: state.authentication && state.authentication.data && state.authentication.data.userName,
    role: state.authentication && state.authentication.data && state.authentication.data.role,
    featureFlags: state.featureFlag && state.featureFlag.data,
    featureFlagsLoaded: state.featureFlag && state.featureFlag.loaded,
    iamData: state.authentication && state.authentication.data && state.authentication.data.iamData
  };
}
function mapDispatchToProps(dispatch) {
  const { guiUrl, enableAvalaraIdentity, featureFlags } = getClientConfig();
  return {
    doLogout: (onSuccess, onFail) => {
      log.debug('doLogout(): ');
      dispatch(AUTHENTICATION_CONTROLLER.doAttemptLogout(onSuccess, onFail));

      // when AI cookie auth is enabled, hit logout route to log out of AI
      if (enableAvalaraIdentity) {
        window.location.href = `${guiUrl}/auth/logout`;
      }
    },
    doLoadDynamicMenu: (enableSideNavigation) => {
      if(featureFlags?EnableDynamicMenu:true)
      {
        log.debug('mapDispatchToProps => doLoadDynamicMenu(): ');
        dispatch(AUTHENTICATION_CONTROLLER.doLoadDynamicMenuItems(enableSideNavigation));
      }
    },
  };
}


Header.defaultProps = {
  hideMenu:false
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Header));
